/**
 * Publish manifest — `skaile.manifest.yaml`
 *
 * What a repo PUBLISHES: the `publisher` / `version` / `assets[]` that used to
 * live in the publication half of `skaile.yaml`, plus a per-asset dependency
 * graph, store-listing metadata, and an optional curator `source:` pointer.
 *
 * ── Two files, two codecs ──────────────────────────────────────────────────
 *
 *   skaile.yaml             → SkWorkspaceConfig   (workspace + consumption)
 *   skaile.manifest.yaml    → SkPublishManifest   (publication)            ← this module
 *
 * A repo can have either file, both, or neither: a pure workspace has only
 * `skaile.yaml`; a pure asset repo (e.g. `ai-assets`) has only
 * `skaile.manifest.yaml`; a repo that is both has both.
 *
 * ── One format, three roles ────────────────────────────────────────────────
 *
 *   First-party    `source:` absent  → bytes live in this repo (`root`/`files`).
 *   Curator/sidecar `source:` present → bytes come from the referenced upstream,
 *                                       verified by `sha256`.
 *
 * `decodeSkaileManifest` mirrors `decodeSkaileYaml`: total (never throws),
 * returns {@link Diagnostic}s. Dep-ref strings are validated with `parseAssetRef`.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { join, resolve } from "node:path";
import { parse, stringify } from "yaml";
import { parseAssetRef } from "./models.js";
import type { Diagnostic } from "./workspace-config.js";

// ── Types ───────────────────────────────────────────────────────────────────

/** One file inside a curated asset, pinned to an exact upstream blob hash. */
export interface ManifestFileEntry {
  path: string;
  /** 64-hex sha256 of the upstream blob. */
  sha256: string;
}

/**
 * One entry in a manifest's `assets:` list. Either `root:` (recursive walk) or
 * `files:` (explicit list, supports globs for first-party; the granular
 * `{path, sha256}` form for curated upstream verification) declares which paths
 * constitute the asset. `version:` overrides the manifest-level version.
 *
 * @docLink packages/core/publish-manifest#asset-entry
 */
export interface AssetEntry {
  kind: string;
  name: string;
  root?: string;
  /**
   * Explicit file listing. First-party manifests use bare `string[]` (globs,
   * fast-glob). Curated entries may use the granular `{path, sha256}` form so
   * the resolver fetches + verifies exact upstream blobs without a full clone.
   */
  files?: string[] | ManifestFileEntry[];
  version?: string;
  /** Per-asset publisher override (e.g. for curator/mirror repos). */
  publisher?: string;

  /**
   * Canonical dep refs (`kind:@<publisher>/name[#version]`) — same grammar as
   * `SkWorkspaceConfig.dependencies`. Authoritative for this asset's resolved
   * deps. (Was `requires` in `.skaile-source.yaml`.)
   */
  dependencies?: string[];

  /**
   * Author-pinned composite sha256 over the asset's `files`/`root` (same
   * deterministic algorithm as the walker + discovery). Optional locally;
   * REQUIRED on store publish. (Was `.skaile-source.yaml` `assets[].sha256`.)
   */
  sha256?: string;

  /**
   * Curator override: this single asset's bytes come from a different upstream
   * than the manifest-level `source`. Absent → inherit manifest `source` (or
   * first-party when that is also absent).
   */
  source?: ManifestSource;

  // ── Store-listing metadata (all optional; fall back to content frontmatter) ──
  description?: string;
  keywords?: string[];
  category?: string;
  license?: string;
  homepage?: string;
}

/**
 * Upstream the bytes come from when a manifest curates a repo it does not own.
 * Absent → first-party (bytes live in the manifest's own repo).
 *
 * @docLink packages/core/publish-manifest#manifest-source
 */
export interface ManifestSource {
  url: string;
  /** 40-char commit the curated bytes are pinned to. */
  commit?: string;
}

/**
 * Signing/attribution recorded when a manifest is published on behalf of others.
 *
 * @docLink packages/core/publish-manifest#manifest-provenance
 */
export interface ManifestProvenance {
  curator: string;
  signed_by?: string;
}

/**
 * Parsed `skaile.manifest.yaml` — what a repo publishes.
 *
 * @docLink packages/core/publish-manifest#sk-publish-manifest
 */
export interface SkPublishManifest {
  publisher?: string;
  version?: string;
  /** Repo-level store defaults inherited by assets unless overridden per-entry. */
  license?: string;
  homepage?: string;
  /**
   * Repo-relative path prefixes holding dev-only assets — excluded from
   * discovery/publish unless `--dev`. (Was `.skaile-source.yaml` `dev_paths`.)
   */
  dev_paths?: string[];
  /**
   * Curator mode: the single upstream all assets in this file come from. A
   * per-asset `source` overrides it. Absent → first-party (own repo).
   */
  source?: ManifestSource;
  /** Present when this manifest is curated/published on behalf of another author. */
  provenance?: ManifestProvenance;
  assets?: AssetEntry[];
}

/** Result of {@link decodeSkaileManifest}: the manifest plus any notes. */
export interface ManifestDecodeResult {
  manifest: SkPublishManifest;
  diagnostics: Diagnostic[];
}

/** The single fixed filename for a publish manifest. */
export const SKAILE_MANIFEST_FILENAME = "skaile.manifest.yaml";

// ── Decode ──────────────────────────────────────────────────────────────────

function parseSource(raw: unknown): ManifestSource | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const s = raw as Record<string, unknown>;
  if (typeof s.url !== "string" || s.url.length === 0) return undefined;
  const out: ManifestSource = { url: s.url };
  if (typeof s.commit === "string" && s.commit.length > 0) out.commit = s.commit;
  return out;
}

function parseProvenance(raw: unknown): ManifestProvenance | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const p = raw as Record<string, unknown>;
  if (typeof p.curator !== "string" || p.curator.length === 0) return undefined;
  const out: ManifestProvenance = { curator: p.curator };
  if (typeof p.signed_by === "string" && p.signed_by.length > 0) out.signed_by = p.signed_by;
  return out;
}

/** Parse `files:` as either `string[]` (globs) or `{path, sha256}[]` (granular). */
function parseFiles(raw: unknown): string[] | ManifestFileEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const strings: string[] = [];
  const granular: ManifestFileEntry[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      strings.push(item);
    } else if (item && typeof item === "object" && !Array.isArray(item)) {
      const f = item as Record<string, unknown>;
      if (typeof f.path === "string" && typeof f.sha256 === "string") {
        granular.push({ path: f.path, sha256: f.sha256 });
      }
    }
  }
  // A manifest uses one form per entry; if both somehow appear, granular wins
  // (it is the verifiable form). Empty → undefined.
  if (granular.length > 0) return granular;
  if (strings.length > 0) return strings;
  return undefined;
}

/** Parse the YAML root, returning either the mapping or an error diagnostic. */
function parseManifestRoot(
  text: string,
): { ok: true; raw: Record<string, unknown> } | { ok: false; diagnostic: Diagnostic } {
  let parsed: unknown;
  try {
    parsed = parse(text) ?? {};
  } catch (err) {
    return {
      ok: false,
      diagnostic: {
        code: "yaml_syntax",
        severity: "error",
        message: `Invalid YAML: ${err instanceof Error ? err.message : String(err)}`,
      },
    };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      ok: false,
      diagnostic: {
        code: "non_object_root",
        severity: "error",
        message: "skaile.manifest.yaml must be a mapping at the top level.",
      },
    };
  }
  return { ok: true, raw: parsed as Record<string, unknown> };
}

/**
 * Strip a SINGLE leading `@` from a publisher and, when one was removed, push a
 * `warning` diagnostic. The `@` is the scope sigil the ref formatter adds — an
 * authored `@acme` would otherwise index as `@@acme` and be un-addable. Bare
 * names are returned untouched.
 */
function normalizePublisher(value: string, path: string, diagnostics: Diagnostic[]): string {
  if (!value.startsWith("@")) return value;
  const stripped = value.slice(1);
  diagnostics.push({
    code: "publisher_leading_sigil",
    severity: "warning",
    message: `publisher "${value}": leading "@" is the scope sigil and is added automatically — use bare "${stripped}"`,
    path,
  });
  return stripped;
}

/** Decode the manifest-level scalar/array/object fields onto `manifest`. */
function decodeManifestFields(
  raw: Record<string, unknown>,
  manifest: SkPublishManifest,
  diagnostics: Diagnostic[],
): void {
  if (typeof raw.publisher === "string" && raw.publisher.length > 0) {
    manifest.publisher = normalizePublisher(raw.publisher, "publisher", diagnostics);
  }
  if (typeof raw.version === "string" && raw.version.length > 0) {
    manifest.version = raw.version;
  }
  if (typeof raw.license === "string" && raw.license.length > 0) {
    manifest.license = raw.license;
  }
  if (typeof raw.homepage === "string" && raw.homepage.length > 0) {
    manifest.homepage = raw.homepage;
  }
  if (Array.isArray(raw.dev_paths)) {
    const paths = raw.dev_paths.filter((p): p is string => typeof p === "string" && p.length > 0);
    if (paths.length > 0) manifest.dev_paths = paths;
  }
  const source = parseSource(raw.source);
  if (source) manifest.source = source;
  const provenance = parseProvenance(raw.provenance);
  if (provenance) manifest.provenance = provenance;
}

/**
 * Validate an asset's `dependencies` via `parseAssetRef`, pushing a `warning`
 * for each malformed ref. Returns the kept (valid) refs.
 */
function decodeAssetDependencies(
  raw: unknown,
  kind: string,
  name: string,
  diagnostics: Diagnostic[],
): string[] {
  const deps: string[] = [];
  if (!Array.isArray(raw)) return deps;
  for (const dep of raw) {
    if (typeof dep !== "string") continue;
    try {
      parseAssetRef(dep);
      deps.push(dep);
    } catch (err) {
      diagnostics.push({
        code: "malformed_dependency",
        severity: "warning",
        message: `assets[${kind}:${name}].dependencies: ${
          err instanceof Error ? err.message : String(err)
        }`,
        path: `assets.${kind}:${name}.dependencies`,
      });
    }
  }
  return deps;
}

/**
 * Decode one `assets[]` item into an {@link AssetEntry}, or `undefined` when the
 * item is not a mapping or lacks both `kind` and `name`. Pushes dependency
 * warnings to `diagnostics`.
 */
function decodeAssetEntry(item: unknown, diagnostics: Diagnostic[]): AssetEntry | undefined {
  if (!item || typeof item !== "object" || Array.isArray(item)) return undefined;
  const a = item as Record<string, unknown>;
  const kind = typeof a.kind === "string" ? a.kind : "";
  const name = typeof a.name === "string" ? a.name : "";
  if (!kind || !name) return undefined;
  const entry: AssetEntry = { kind, name };
  if (typeof a.root === "string") entry.root = a.root;
  const files = parseFiles(a.files);
  if (files) entry.files = files;
  if (typeof a.version === "string") entry.version = a.version;
  if (typeof a.publisher === "string")
    entry.publisher = normalizePublisher(
      a.publisher,
      `assets.${kind}:${name}.publisher`,
      diagnostics,
    );
  if (typeof a.sha256 === "string") entry.sha256 = a.sha256;
  const assetSource = parseSource(a.source);
  if (assetSource) entry.source = assetSource;

  const deps = decodeAssetDependencies(a.dependencies, kind, name, diagnostics);
  if (deps.length > 0) entry.dependencies = deps;

  if (typeof a.description === "string") entry.description = a.description;
  if (Array.isArray(a.keywords)) {
    const kw = a.keywords.filter((k): k is string => typeof k === "string");
    if (kw.length > 0) entry.keywords = kw;
  }
  if (typeof a.category === "string") entry.category = a.category;
  if (typeof a.license === "string") entry.license = a.license;
  if (typeof a.homepage === "string") entry.homepage = a.homepage;

  return entry;
}

/** Decode the `assets[]` list onto `manifest`, collecting dependency warnings. */
function decodeManifestAssets(
  raw: Record<string, unknown>,
  manifest: SkPublishManifest,
  diagnostics: Diagnostic[],
): void {
  if (!Array.isArray(raw.assets)) return;
  const entries: AssetEntry[] = [];
  for (const item of raw.assets) {
    const entry = decodeAssetEntry(item, diagnostics);
    if (entry) entries.push(entry);
  }
  if (entries.length > 0) manifest.assets = entries;
}

/**
 * Decode `skaile.manifest.yaml` text into a {@link SkPublishManifest} plus
 * {@link Diagnostic}s. Total — never throws: a YAML syntax error or non-object
 * root is reported as an `error`-severity diagnostic with an empty manifest.
 * Malformed `assets[].dependencies` refs are reported as `warning`s (validated
 * via `parseAssetRef`) rather than silently dropped.
 *
 * Pair with {@link encodeSkaileManifest} for round-tripping.
 * @docLink packages/core/publish-manifest#decode-skaile-manifest
 */
export function decodeSkaileManifest(text: string): ManifestDecodeResult {
  const root = parseManifestRoot(text);
  if (!root.ok) {
    return { manifest: {}, diagnostics: [root.diagnostic] };
  }

  const manifest: SkPublishManifest = {};
  const diagnostics: Diagnostic[] = [];
  decodeManifestFields(root.raw, manifest, diagnostics);
  decodeManifestAssets(root.raw, manifest, diagnostics);

  return { manifest, diagnostics };
}

// ── Encode ──────────────────────────────────────────────────────────────────

/** Fixed top-level key order for {@link encodeSkaileManifest}. */
const MANIFEST_KEY_ORDER: (keyof SkPublishManifest)[] = [
  "publisher",
  "version",
  "license",
  "homepage",
  "dev_paths",
  "source",
  "provenance",
  "assets",
];

/** Fixed per-asset key order for {@link encodeSkaileManifest}. */
const ASSET_KEY_ORDER: (keyof AssetEntry)[] = [
  "kind",
  "name",
  "source",
  "root",
  "files",
  "version",
  "publisher",
  "sha256",
  "dependencies",
  "description",
  "keywords",
  "category",
  "license",
  "homepage",
];

function orderedAsset(a: AssetEntry): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of ASSET_KEY_ORDER) {
    const value = a[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Encode a {@link SkPublishManifest} to canonical `skaile.manifest.yaml` text:
 * deterministic key order, only defined fields, `lineWidth: 120` (mirrors
 * `encodeSkaileYaml`).
 * @docLink packages/core/publish-manifest#encode-skaile-manifest
 */
export function encodeSkaileManifest(manifest: SkPublishManifest): string {
  const out: Record<string, unknown> = {};
  for (const key of MANIFEST_KEY_ORDER) {
    const value = manifest[key];
    if (value === undefined) continue;
    if (key === "assets" && Array.isArray(value)) {
      out.assets = (value as AssetEntry[]).map(orderedAsset);
    } else {
      out[key] = value;
    }
  }
  return stringify(out, { lineWidth: 120 });
}

// ── File I/O ──────────────────────────────────────────────────────────────────

/**
 * Load and decode `<dir>/skaile.manifest.yaml`. Returns `null` when the file is
 * absent or unparseable (mirrors `loadSkWorkspaceConfig`'s tolerant contract).
 * @docLink packages/core/publish-manifest#load-sk-publish-manifest
 */
export function loadSkPublishManifest(dir: string): SkPublishManifest | null {
  const filePath = join(resolve(dir), SKAILE_MANIFEST_FILENAME);
  if (!existsSync(filePath)) return null;
  let raw: string;
  try {
    raw = readFileSync(filePath, "utf-8");
  } catch {
    return null;
  }
  const { manifest, diagnostics } = decodeSkaileManifest(raw);
  if (diagnostics.some((d) => d.code === "yaml_syntax" || d.code === "non_object_root")) {
    return null;
  }
  return manifest;
}

/**
 * Encode and write a manifest to `<dir>/skaile.manifest.yaml`, creating the
 * directory if needed. Returns the absolute path written.
 * @docLink packages/core/publish-manifest#save-sk-publish-manifest
 */
export function saveSkPublishManifest(dir: string, manifest: SkPublishManifest): string {
  const resolvedDir = resolve(dir);
  const filePath = join(resolvedDir, SKAILE_MANIFEST_FILENAME);
  if (!existsSync(resolvedDir)) {
    mkdirSync(resolvedDir, { recursive: true });
  }
  writeFileSync(filePath, encodeSkaileManifest(manifest));
  return filePath;
}
